<template>
  <session-content-container :title="title">
    <template #left>
      <v-btn
        color="accent"
        small
        block
        @click="exportPlayers"
      >
        <v-icon left>
          mdi-file-excel
        </v-icon>
        {{ $t('general.export') }}
      </v-btn>
    </template>
    <template #right>
      <open-close-session-button :session="session" />
    </template>
    <v-data-table
      v-if="session"
      :headers="headers"
      :items="items"
      :loading="loading"
      :loading-text="$t('general.loading')"
      disable-pagination
      hide-default-footer
      multi-sort
    >
      <template v-slot:[`item.connected`]="{ item }">
        <v-icon v-if="item.connected" color="accent">
          mdi-check-circle
        </v-icon>
      </template>
    </v-data-table>
  </session-content-container>
</template>

<script>
import _throttle from 'lodash/throttle'
import Repositories from 'src/repositories'
import { mapActions } from 'vuex'
import { format, parseISO } from 'date-fns'
import { getLanguageText } from 'src/plugins/i18n'
import { downloadExcelFile } from 'src/services/exportExcelService'
import SessionContentContainer from './SessionContentContainer'
import OpenCloseSessionButton from '../components/session-menu/OpenCloseSessionButton'

export default {
  name: 'PlayersContainer',
  components: {
    OpenCloseSessionButton,
    SessionContentContainer,
  },
  props: {
    sessionId: {
      type: String,
      required: true,
    },
    session: {
      type: Object,
      default: null,
    },
  },
  data: () => ({
    loading: false,
  }),
  computed: {
    title () {
      return this.session?.code
        ? this.$t('animation.players.title', { sessionCode: this.session.code })
        : this.$t('general.loading')
    },
    headers () {
      return [
        {
          text: this.$t('animation.players.fields.sessionCode'),
          value: 'sessionCode',
          align: 'center',
          sortable: false,
          width: '110px',
        },
        {
          text: this.$t('animation.players.fields.playerCode'),
          value: 'code',
          align: 'center',
          sortable: true,
          width: '80px',
        },
        {
          text: this.$t('animation.players.fields.name'),
          value: 'name',
          align: 'center',
          sortable: true,
        },
        {
          text: this.$t('animation.players.fields.email'),
          value: 'email',
          align: 'center',
          sortable: true,
        },
        {
          text: this.$t('animation.players.fields.connected'),
          value: 'connected',
          align: 'center',
          sortable: true,
          width: '110px',
        },
      ]
    },
    items () {
      return this.session.playerList
        .map(item => ({ sessionCode: this.session.code, ...item }))
        .sort((a, b) => a.code.localeCompare(b.code))
    },
  },
  sockets: {
    trainingSessionPlayerLoggedIn ({ idSession }) {
      if (this.sessionId === idSession) {
        this.showSuccessNotification(this.$t('animation.players.socket.login'))
        this.refreshSession()
      }
    },
    trainingSessionPlayerLoggedOut ({ idSession }) {
      if (this.sessionId === idSession) {
        if (this.session?.status !== 'closed') {
          this.showErrorNotification(this.$t('animation.players.socket.logout'))
        }
        this.refreshSession()
      }
    },
  },
  methods: {
    ...mapActions('ui', {
      showSuccessNotification: 'showSuccessNotification',
      showErrorNotification: 'showErrorNotification',
    }),
    refreshSession: _throttle(async function refreshSession () {
      this.loading = true
      const session = await Repositories.trainingSession.session.get(this.sessionId)
      this.$emit('update:session', session)
      this.loading = false
    }, 200, { leading: true, trailing: true }),
    exportPlayers () {
      const sessionDetails = {
        sessionName: this.session.name,
        sessionCode: this.session.code,
        dateCreate: format(parseISO(this.session.dateCreate), this.$t('date.dateAndTime')),
        lang: getLanguageText(this.session.lang),
      }
      downloadExcelFile({
        sheetList: [
          {
            headers: [
              {
                header: this.$t('animation.players.export.sessionName'),
                key: 'sessionName',
                width: 30,
              },
              {
                header: this.$t('animation.players.export.sessionCode'),
                key: 'sessionCode',
                width: 15,
              },
              {
                header: this.$t('animation.players.export.sessionDateCreate'),
                key: 'dateCreate',
                width: 20,
              },
              {
                header: this.$t('general.language'),
                key: 'lang',
                width: 15,
              },
              {
                header: this.$t('animation.players.export.playerCode'),
                key: 'code',
                width: 15,
              },
              {
                header: this.$t('animation.players.export.playerName'),
                key: 'name',
                width: 25,
              },
              {
                header: this.$t('animation.players.export.playerEmail'),
                key: 'email',
                width: 40,
              },
            ],
            rows: this.session.playerList
              .map(player => ({ ...player, ...sessionDetails })),
            sheetname: this.$t('animation.players.export.sheetname', { name: this.session.name }),
          },
        ],
        filename: this.$t('animation.players.export.filename', { name: this.session.name }),
      })
    },
  },
}
</script>
